import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import { AlertTriangle, Info } from "lucide-react";

const ConfirmContext = createContext(() => Promise.resolve(false));

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false });
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setState({ open: true, ...opts });
  }), []);

  const close = (val) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) { resolver.current(val); resolver.current = null; }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent className="glass border border-slate-800 text-slate-100" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-slate-100 flex items-center gap-2">
              {state.tone === "neutral" ? (
                <span className="w-9 h-9 rounded-full bg-sky-500/10 border border-sky-500/30 flex items-center justify-center"><Info className="w-4 h-4 text-sky-300" /></span>
              ) : (
                <span className="w-9 h-9 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-red-400" /></span>
              )}
              {state.title || "Are you certain?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400 pt-1">
              {state.description || "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="confirm-cancel"
              onClick={() => close(false)}
              className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white"
            >
              {state.cancelText || "Stand down"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-accept"
              onClick={() => close(true)}
              className={state.tone === "neutral" ? "bg-sky-500 hover:bg-sky-400 text-slate-950 border-0" : "bg-red-600 hover:bg-red-600 text-white border-0"}
            >
              {state.confirmText || "Proceed"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
